<script setup lang="ts">
  import type { MessageResponse } from '~/models/chat';
  import { useClipboard } from '@vueuse/core';
  import { isMessageDisliked, isMessageLiked } from '@/utils/content-utils';

  const props = defineProps({
    message: {
      type: Object as PropType<MessageResponse>,
      required: true
    }
  });

  const emit = defineEmits(['like', 'dislike', 'regenerate', 'acceptWebsite', 'declineWebsite']);

  const { t } = useI18n();

  const isIda = computed(() => props.message.author.username == 'Ida' || props.message.author.username == 'CoMind');
  const isChameleonApprove = computed(() => props.message.chameleonApprove ?? false);
  const username = computed(() => (isIda.value ? 'CoMind' : isChameleonApprove.value ? undefined : t('chat.me')));

  const isLiked = computed(() => isMessageLiked(props.message));
  const isDisliked = computed(() => isMessageDisliked(props.message));

  const chatStore = useChatStore();
  const { chatColor1, chatColor2, isChameleonMode, isLastSearch } = storeToRefs(chatStore);
  const color1 = computed(() => (isChameleonMode.value ? chatColor1.value : '#989898'));
  const color2 = computed(() => (isChameleonMode.value ? chatColor2.value : '#FFFFFF'));

  const declineBtnText = computed(() => (isLastSearch.value ? t('chat.chameleonMessage.declineAndStop') : t('chat.chameleonMessage.decline')));

  const noSiteImage = computed(() => !props.message.chameleonImage);
  const siteImage = computed(() => props.message.chameleonImage ?? '/no-image.svg');

  const { copy } = useClipboard({
    legacy: true
  });

  const like = () => {
    if (isLiked.value || isDisliked.value) return;

    emit('like', props.message.id);
  };

  const dislike = () => {
    if (isLiked.value || isDisliked.value) return;

    emit('dislike', props.message.id);
  };

  const acceptWebsite = () => {
    if (isChameleonMode.value) return;

    emit('acceptWebsite');
  };

  const declineWebsite = () => {
    if (isChameleonMode.value) return;

    emit('declineWebsite');
  };
</script>

<template>
  <div
    :class="[
      'chat-message',
      {
        'chat-message--ida': isIda,
        'chat-message--liked': isLiked,
        'chat-message--disliked': isDisliked,
        'chat-message--chameleon': isChameleonMode,
        'chat-message--chameleon-approve': isChameleonApprove,
        'chat-message--chameleon-declined': message.chameleonDeclined
      }
    ]"
    :style="{ '--chat-bg-color-1': color1, '--chat-bg-color-2': color2 }">
    <div class="chat-message__heading">
      <div class="chat-message__icon"></div>
      <div class="chat-message__author">{{ username ? `${username}, ` : '' }}{{ $d(message.timestamp, 'long') }}</div>
    </div>
    <div class="chat-message__body">
      <div class="chat-message__bg">
        <div
          v-if="isChameleonApprove"
          class="chat-message__title">
          <div></div>
          <div>{{ $t('chat.chameleonMessage.title') }}</div>
        </div>
        <div class="chat-message__content">
          <m-d-c :value="message.content" />
        </div>
        <div
          v-if="isChameleonApprove"
          class="chat-message__site-info">
          <div
            :class="{ 'no-image': noSiteImage }"
            :style="{ '--site-img': `url(${siteImage})` }" />
          <div>
            <div>{{ message.chameleonUrl }}</div>
            <div>{{ message.chameleonTitle }}</div>
            <div>{{ message.chameleonDescription }}</div>
          </div>
        </div>
        <div
          v-if="isIda && message.event === 'MESSAGE_UPDATE'"
          class="chat-message__footer">
          <div>
            <button
              class="chat-message__regenerate"
              aria-label="regenerate"
              @click.prevent.stop="$emit('regenerate', message.id)">
              {{ $t('chat.regenerateAnswer') }}
            </button>
          </div>
          <div>
            <button
              aria-label="dislike"
              @click.prevent.stop="dislike" />
            <button
              aria-label="like"
              @click.prevent.stop="like" />
            <button
              aria-label="copy"
              @click.prevent.stop="copy(message.content)" />
          </div>
        </div>
        <div
          v-if="isChameleonApprove && !isChameleonMode && !message.chameleonDeclined"
          class="chat-message__approve-footer">
          <button @click.prevent.stop="acceptWebsite">{{ $t('chat.chameleonMessage.accept') }}</button>
          <button @click.prevent.stop="declineWebsite">{{ declineBtnText }}</button>
        </div>
      </div>
    </div>
  </div>
</template>
